/**
 * Firestore 태스크 실시간 구독 Hook
 * Phase A5: 실시간 UI 업데이트 구현
 */

import { useState, useEffect } from 'react';
import { dbService } from '../firebaseInstance';

/**
 * 사용자의 활성 태스크 실시간 구독
 * @param {string} userID - 사용자 ID
 * @returns {Object} { tasks, loading, error }
 */
export const useActiveTasksSubscription = (userID) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userID) {
      setLoading(false);
      setError('사용자 ID가 없습니다.');
      return;
    }

    console.log(`🔄 Subscribing to active tasks for user: ${userID}`);

    const unsubscribe = dbService
      .collection('user')
      .doc(userID)
      .collection('active_tasks')
      .orderBy('executeAt', 'asc')
      .onSnapshot(
        (snapshot) => {
          const taskList = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              // Cloud Tasks 호환성을 위한 변환
              name: doc.data().cloudTaskId || `firestore-task-${doc.id}`,
              scheduleTime: {
                seconds: Math.floor(new Date(doc.data().executeAt + ' GMT+0900').getTime() / 1000)
              },
              createTime: {
                seconds: Math.floor(doc.data().createdAt / 1000)
              },
              _sortTime: Math.floor(new Date(doc.data().executeAt + ' GMT+0900').getTime() / 1000)
            }))
            // 클라이언트 사이드에서 완료된 태스크 필터링
            .filter(task => {
              const isActive = task.status === 'scheduled' || 
                              task.status === 'executing' || 
                              task.status === 'pending_creation' ||
                              task.status === 'retrying';
              
              console.log(`Task ${task.id}: status="${task.status}", isActive=${isActive}`);
              return isActive;
            });

          console.log(`✅ Active tasks updated: ${taskList.length} tasks`);
          setTasks(taskList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Active tasks subscription error:', err);
          setError(`실시간 업데이트 오류: ${err.message}`);
          setLoading(false);
        }
      );

    return () => {
      console.log(`🔄 Unsubscribing from active tasks for user: ${userID}`);
      unsubscribe();
    };
  }, [userID]);

  return { tasks, loading, error };
};

/**
 * 사용자의 완료된 태스크 실시간 구독 (최근 20개)
 * @param {string} userID - 사용자 ID
 * @returns {Object} { tasks, loading, error }
 */
export const useCompletedTasksSubscription = (userID) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userID) {
      setLoading(false);
      setError('사용자 ID가 없습니다.');
      return;
    }

    console.log(`🔄 Subscribing to completed tasks for user: ${userID}`);

    const unsubscribe = dbService
      .collection('user')
      .doc(userID)
      .collection('completed_tasks')
      .orderBy('completedAt', 'desc')
      .limit(20)
      .onSnapshot(
        (snapshot) => {
          const taskList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log(`✅ Completed tasks updated: ${taskList.length} tasks`);
          setTasks(taskList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Completed tasks subscription error:', err);
          setError(`완료 목록 업데이트 오류: ${err.message}`);
          setLoading(false);
        }
      );

    return () => {
      console.log(`🔄 Unsubscribing from completed tasks for user: ${userID}`);
      unsubscribe();
    };
  }, [userID]);

  return { tasks, loading, error };
};

/**
 * 사용자의 태스크 메타데이터 실시간 구독
 * @param {string} userID - 사용자 ID
 * @returns {Object} { metadata, loading, error }
 */
export const useTaskMetadataSubscription = (userID) => {
  const [metadata, setMetadata] = useState({
    totalTasks: 0,
    scheduledTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    lastActivity: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userID) {
      setLoading(false);
      setError('사용자 ID가 없습니다.');
      return;
    }

    console.log(`🔄 Subscribing to task metadata for user: ${userID}`);

    const unsubscribe = dbService
      .collection('user')
      .doc(userID)
      .collection('metadata')
      .doc('tasks')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            console.log(`✅ Task metadata updated:`, data);
            setMetadata(data);
          } else {
            console.log(`ℹ️ No metadata found for user: ${userID}`);
            setMetadata({
              totalTasks: 0,
              scheduledTasks: 0,
              completedTasks: 0,
              failedTasks: 0,
              lastActivity: null
            });
          }
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Task metadata subscription error:', err);
          setError(`메타데이터 업데이트 오류: ${err.message}`);
          setLoading(false);
        }
      );

    return () => {
      console.log(`🔄 Unsubscribing from task metadata for user: ${userID}`);
      unsubscribe();
    };
  }, [userID]);

  return { metadata, loading, error };
};

/**
 * 모든 태스크 관련 실시간 데이터를 통합 제공하는 Hook
 * @param {string} userID - 사용자 ID
 * @returns {Object} 통합 태스크 데이터
 */
export const useTasksFullSubscription = (userID) => {
  const activeTasks = useActiveTasksSubscription(userID);
  const completedTasks = useCompletedTasksSubscription(userID);
  const taskMetadata = useTaskMetadataSubscription(userID);

  const isLoading = activeTasks.loading || completedTasks.loading || taskMetadata.loading;
  const hasError = activeTasks.error || completedTasks.error || taskMetadata.error;

  return {
    activeTasks: activeTasks.tasks,
    completedTasks: completedTasks.tasks,
    metadata: taskMetadata.metadata,
    loading: isLoading,
    error: hasError || null,
    // 편의 속성들
    totalActiveTasks: activeTasks.tasks.length,
    totalCompletedTasks: completedTasks.tasks.length,
    isRealtime: !isLoading && !hasError
  };
};

/**
 * pending_tasks 실시간 구독 (디버깅/모니터링용)
 * @param {string} userID - 사용자 ID
 * @returns {Object} { pendingTasks, loading, error }
 */
export const usePendingTasksSubscription = (userID) => {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userID) {
      setLoading(false);
      return;
    }

    console.log(`🔄 Subscribing to pending tasks for user: ${userID}`);

    const unsubscribe = dbService
      .collection('user')
      .doc(userID)
      .collection('pending_tasks')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const taskList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log(`⏳ Pending tasks updated: ${taskList.length} tasks`);
          setPendingTasks(taskList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Pending tasks subscription error:', err);
          setError(`대기 목록 업데이트 오류: ${err.message}`);
          setLoading(false);
        }
      );

    return () => {
      console.log(`🔄 Unsubscribing from pending tasks for user: ${userID}`);
      unsubscribe();
    };
  }, [userID]);

  return { pendingTasks, loading, error };
};

export default {
  useActiveTasksSubscription,
  useCompletedTasksSubscription,
  useTaskMetadataSubscription,
  useTasksFullSubscription,
  usePendingTasksSubscription
};